import { isAxiosError } from 'axios';

/**
 * Trigger a browser download of a blob, then revoke the object URL.
 *
 * Creates an object URL, clicks a throwaway anchor to trigger the save, then
 * revokes the URL - an unrevoked object URL keeps the whole blob alive for
 * the life of the document, which adds up across repeated exports.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Read a Blob as text across environments (`Blob.text()` is absent in some
 * jsdom versions). Falls back to `FileReader` rather than throwing - the
 * point of reading the Blob at all is to explain a failure, and an
 * unreadable body must not become a second, more confusing failure.
 */
export function readBlobText(blob: Blob): Promise<string> {
  if (typeof blob.text === 'function') return blob.text();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('Could not read response body'));
    reader.readAsText(blob);
  });
}

/** Read the filename out of a Content-Disposition header, falling back when absent. */
export function filenameFrom(contentDisposition: string | undefined, fallback: string): string {
  const match = contentDisposition?.match(/filename="?([^";]+)"?/);
  return match?.[1] ?? fallback;
}

/**
 * Parse a blob-shaped axios error's response body back into the structured
 * `{ code, detail }` (or plain-text) shape every error helper expects, and
 * return the same error for the caller to rethrow.
 *
 * `responseType: 'blob'` applies to error responses too, so a failed blob
 * download (a 400, 403, or 404) arrives with `error.response.data` as a Blob
 * containing JSON rather than the parsed object `getApiErrorMessage` and
 * `isAxiosError` readers expect. This mutates `error.response.data` in place
 * so those readers keep working unchanged.
 */
export async function unwrapBlobErrorBody(error: unknown): Promise<unknown> {
  if (isAxiosError(error) && error.response?.data instanceof Blob) {
    try {
      const text = await readBlobText(error.response.data);
      try {
        error.response.data = JSON.parse(text);
      } catch {
        error.response.data = text ? { message: text } : undefined;
      }
    } catch {
      error.response.data = undefined;
    }
  }
  return error;
}
